import { inject, Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { EPlatform } from '@enums/campaign.enum';
import { IAnalyticsNode, ICampaignSocialContent } from '@interfaces/campaign-analytics.interface';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { SocialContentUtilitiesService } from '@utilities/social-content-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { differenceInMinutes } from 'date-fns';
import { ICtaAnalyticsData } from './cta-analytics-table.interface';

@Injectable({
  providedIn: 'root',
})
export class CtaAnalyticsTableService {
  private readonly socialContentUtilitiesService = inject(SocialContentUtilitiesService);

  public getPercentage(currentDeliverables: number, totalDeliverables: number): number {
    return Number(((currentDeliverables / totalDeliverables) * 100).toFixed(2));
  }

  public getDuration(start: string, end: string | TNil): number {
    return differenceInMinutes(new Date(end ?? new Date()), new Date(start));
  }

  public getThumbnailUrl(url: string | undefined, width = 112, height = 64): string {
    return ImageUtilitiesService.replaceImageDimensions(url, width, height) ?? '';
  }

  public getSocialContentDependentTableData(
    analyticsNode: IAnalyticsNode,
    content?: ICampaignSocialContent | null,
  ): Pick<ICtaAnalyticsData, 'url' | 'content'> {
    if (!content || !content.socialContent) {
      return {
        content: null,
        url: '',
      };
    }

    const originalUrl = this.socialContentUtilitiesService.getContentUrl(
      analyticsNode.platformFeature,
      content.socialContent,
    );
    const platform = SocialUtilitiesService.getPlatform(analyticsNode.platformFeature);

    return {
      content: content ? { ...content, ctas: Array.from(content.ctas.values()) } : null,
      url:
        analyticsNode.isLive && platform === EPlatform.twitch
          ? SocialUtilitiesService.generateSocialUrl(platform, content.socialContent?.channelId ?? '')
          : originalUrl,
    };
  }
}
